import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalyticsData, getAnalytics } from '../api/client';
import { ErrorState, EmptyState } from '../components/States';
import { StatsSkeleton } from '../components/Skeleton';
import { formatLongDate } from '../lib/date';
import './ProgressPage.css';

interface ProgressPageProps {
  userId: number;
  onOpenWorkout: (id: number) => void;
}

const formatPace = (seconds: number | null | undefined) => {
  if (seconds == null) return '—';
  return `${Math.floor(seconds / 60)}′${String(Math.round(seconds % 60)).padStart(2, '0')}″`;
};
const formatTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = Math.round(seconds % 60);
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}` : `${minutes}:${String(rest).padStart(2, '0')}`;
};
const MetricBars: React.FC<{
  data: AnalyticsData['trends'];
  field: 'paceAvgSecPerKm' | 'hrAvg' | 'cadenceAvg' | 'score';
  inverse?: boolean;
  format: (value: number) => string;
}> = ({ data, field, inverse, format }) => {
  const points = data.slice(-12).filter(item => typeof item[field] === 'number');
  const values = points.map(item => item[field] as number);
  const min = Math.min(...values);
  const max = Math.max(...values);
  return (
    <div className="metric-bars" role="img" aria-label={`График: ${points.map(item => format(item[field] as number)).join(', ')}`}>
      {points.map(item => {
        const value = item[field] as number;
        const normalized = max === min ? 0.6 : (value - min) / (max - min);
        const height = 28 + (inverse ? 1 - normalized : normalized) * 72;
        return (
          <button
            key={`${field}-${item.workoutId}`}
            className="metric-bar-column"
            style={{ '--bar-height': `${height}%` } as React.CSSProperties}
            title={`${formatLongDate(item.date)}: ${format(value)}`}
          >
            <span className="metric-bar-value">{format(value)}</span>
            <span className="metric-bar" />
            <small>{item.date.slice(5).replace('-', '.')}</small>
          </button>
        );
      })}
    </div>
  );
};

export const ProgressPage: React.FC<ProgressPageProps> = ({ userId, onOpenWorkout }) => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<'score' | 'pace' | 'heart' | 'cadence'>('score');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getAnalytics(userId));
    }
    catch (err: any) { setError(err?.message || 'Не удалось загрузить прогресс'); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { if (userId) load(); }, [userId, load]);
  const comparison = data?.latestComparison;
  const latest = useMemo(() => data?.trends[data.trends.length - 1], [data]);

  return (
    <div className="progress-page">
      <header className="progress-header">
        <p>RUN LAB / PERFORMANCE</p>
        <h1>Твой прогресс</h1>
        <span>{data ? `На основе ${data.summary.workouts} пробежек` : 'Персональные показатели и скоринг'}</span>
      </header>

      {loading ? <StatsSkeleton /> : error ? <ErrorState message={error} onRetry={load} /> : !data || data.trends.length === 0 ? (
        <EmptyState icon="—" title="Пока нечего сравнивать" hint="Добавьте первую пробежку, чтобы увидеть динамику и получить оценку." />
      ) : <>
      <div className="progress-summary">
        <div className="summary-score"><strong>{data.summary.avgScore ? Math.round(data.summary.avgScore) : '—'}</strong><span>средний скор</span></div>
        <div><strong>{data.summary.distanceKm.toFixed(1)}</strong><span>км всего</span></div>
        <div><strong>{formatPace(data.summary.avgPaceSecPerKm)}</strong><span>ср. темп</span></div>
        <div><strong>{data.summary.avgHeartRate ? Math.round(data.summary.avgHeartRate) : '—'}</strong><span>ср. пульс</span></div>
      </div>

      <details className="progress-card progress-disclosure" open>
        <summary className="progress-card-header">
          <div><h2>Динамика</h2><p>Последние 12 тренировок</p></div>
          <i />
        </summary>
        <div className="progress-disclosure-body">
          <div className="metric-switch">
            <button className={metric === 'score' ? 'active' : ''} onClick={() => setMetric('score')}>Скор</button>
            <button className={metric === 'pace' ? 'active' : ''} onClick={() => setMetric('pace')}>Темп</button>
            <button className={metric === 'heart' ? 'active' : ''} onClick={() => setMetric('heart')}>Пульс</button>
            <button className={metric === 'cadence' ? 'active' : ''} onClick={() => setMetric('cadence')}>Каденс</button>
          </div>
        {metric === 'score' && <MetricBars data={data.trends} field="score" format={value => String(Math.round(value))} />}
        {metric === 'pace' && <MetricBars data={data.trends} field="paceAvgSecPerKm" inverse format={formatPace} />}
        {metric === 'heart' && <MetricBars data={data.trends} field="hrAvg" format={value => String(Math.round(value))} />}
        {metric === 'cadence' && <MetricBars data={data.trends} field="cadenceAvg" format={value => String(Math.round(value))} />}
        </div>
      </details>

      <details className="progress-section-disclosure">
        <summary><div><h2>Нагрузка и выполнение плана</h2><p>{data.trainingLoad.status} · выполнение {data.planAdherence.score ?? '—'}/100</p></div><i /></summary>
      <section className="performance-duo">
        <article className="load-card">
          <span>НАГРУЗКА / 7 ДНЕЙ</span>
          <h2>{data.trainingLoad.status}</h2>
          <div className="load-distance"><strong>{data.trainingLoad.currentDistanceKm}</strong><small>км сейчас</small><i>vs {data.trainingLoad.previousDistanceKm} км</i></div>
          <p>{data.trainingLoad.recommendation}</p>
          <div className="load-meta"><span>Коэффициент {data.trainingLoad.ratio ?? '—'}</span><span>Монотонность {data.trainingLoad.monotony}</span></div>
        </article>
        <article className="adherence-card">
          <span>ВЫПОЛНЕНИЕ ПЛАНА</span>
          <h2>{data.planAdherence.score ?? '—'}<small>/100</small></h2>
          <p>{data.planAdherence.completed} из {data.planAdherence.planned} запланированных тренировок выполнено</p>
          <div className="adherence-days">
            {data.planAdherence.items.slice(0, 7).reverse().map(item => (
              <i key={item.date} className={item.status} title={`${item.date}: ${item.focus}`} />
            ))}
          </div>
        </article>
      </section>
      </details>

      <details className="records-card progress-section-disclosure">
        <summary><div><h2>Личные ориентиры</h2><p>Лучшие результаты из загруженных отчётов</p></div><i /></summary>
        <div className="records-grid">
          {data.personalRecords.distances.map(record => (
            <button key={record.distanceKm} onClick={() => onOpenWorkout(record.workoutId)}>
              <span>{record.distanceKm === 21.1 ? '21,1' : record.distanceKm} км</span>
              <strong>{formatTime(record.estimatedTimeSeconds)}</strong>
              <small>{formatPace(record.paceSecPerKm)} /км</small>
            </button>
          ))}
          {data.personalRecords.longest && (
            <button onClick={() => onOpenWorkout(data.personalRecords.longest!.workoutId)}>
              <span>Самая длинная</span><strong>{data.personalRecords.longest.distanceKm?.toFixed(1)} км</strong><small>{data.personalRecords.longest.date}</small>
            </button>
          )}
          {data.personalRecords.bestScore && (
            <button onClick={() => onOpenWorkout(data.personalRecords.bestScore!.workoutId)}>
              <span>Лучший Run Score</span><strong>{data.personalRecords.bestScore.score}</strong><small>из 100</small>
            </button>
          )}
        </div>
      </details>

      {comparison && latest && (
        <details className="comparison-card progress-section-disclosure">
          <summary><div><h2>Сравнение похожих дистанций</h2><p>{latest.distanceKm?.toFixed(2)} км против последних {comparison.comparableCount}</p></div><i /></summary>
          {comparison.comparableCount > 0 ? (
            <div className="comparison-grid">
              <div className={(comparison.paceDeltaSec ?? 0) <= 0 ? 'positive' : 'negative'}>
                <strong>{comparison.paceDeltaSec == null ? '—' : `${comparison.paceDeltaSec > 0 ? '+' : ''}${Math.round(comparison.paceDeltaSec)} с`}</strong>
                <span>темп / км</span>
              </div>
              <div className={(comparison.heartRateDelta ?? 0) <= 0 ? 'positive' : 'neutral'}>
                <strong>{comparison.heartRateDelta == null ? '—' : `${comparison.heartRateDelta > 0 ? '+' : ''}${Math.round(comparison.heartRateDelta)}`}</strong>
                <span>пульс</span>
              </div>
              <div className={(comparison.cadenceDelta ?? 0) >= 0 ? 'positive' : 'neutral'}>
                <strong>{comparison.cadenceDelta == null ? '—' : `${comparison.cadenceDelta > 0 ? '+' : ''}${Math.round(comparison.cadenceDelta)}`}</strong>
                <span>каденс</span>
              </div>
            </div>
          ) : <p className="comparison-empty">Добавьте ещё несколько пробежек похожей длины.</p>}
          <button className="open-latest-button" onClick={() => onOpenWorkout(comparison.workoutId)}>Открыть последнюю тренировку →</button>
        </details>
      )}
      </>}
    </div>
  );
};
